// src/services/AuthContext.tsx
import React, { createContext, useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { api } from './Axios';

interface Organization {
  _id?: string;  // Permitir undefined para consistencia con Configuracion.tsx
  name?: string;
  razonSocial?: string;
  NIT?: string;
  direccion?: string;
  ciudad?: string;
  telefonoContacto?: string;
  responsableIVA?: boolean;
  createdAt?: string;
  identificador?: string;
  pin?: string;
  setId?: string;
}

interface UserData {
  nombre: string;
  email: string;
  role: string;
  isVerified: boolean;
  organization?: Organization;
  subscription?: {
    plan: string;
    status: string;
    startDate: string;
    endDate: string;
  }
}

interface AuthContextProps {
  isAuthenticated: boolean | null;
  hasOrganization: boolean | null;
  hasSubscription: boolean | null;
  subscriptionStatus: 'active' | 'inactive' | null; 
  userData: UserData | null;
  syncAuthStatus: () => Promise<void>;
  updateUserData: (newUserData: Partial<UserData>) => void; // Añadido aquí
  logout: () => void;
}

const AuthContext = createContext<AuthContextProps | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [hasOrganization, setHasOrganization] = useState<boolean | null>(null);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [hasSubscription, setHasSubscription] = useState<boolean | null>(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState<'active' | 'inactive' | null>(null);

  const syncAuthStatus = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const response = await api.get('/api/auth/user');
        const { organization, ...userDetails } = response.data;
  
        let hasSubscription = false;
        let subscriptionStatus: 'active' | 'inactive' | null = null;
  
        if (organization) {
          try {
            const subscriptionResponse = await api.get('/api/subscriptions');
            hasSubscription = !!subscriptionResponse.data.subscription;
            subscriptionStatus = subscriptionResponse.data.subscription?.status || null;
            setUserData({ ...userDetails, organization, subscription: subscriptionResponse.data.subscription });
          } catch (error: any) {
            if (error.response?.status === 404) {
              hasSubscription = false;
              subscriptionStatus = null;
            } else {
              throw error;
            }
          }
        }
  
        setUserData({ ...userDetails, organization });
        setIsAuthenticated(true);
        setHasOrganization(!!organization);
        setHasSubscription(hasSubscription);
        setSubscriptionStatus(subscriptionStatus);
      } catch (error) {
        setIsAuthenticated(false);
        setHasOrganization(null);
        setHasSubscription(false);
        setSubscriptionStatus(null);
        setUserData(null);
        localStorage.removeItem('token');
      }
    } else {
      setIsAuthenticated(false);
      setHasOrganization(null);
      setHasSubscription(false);
      setSubscriptionStatus(null);
      setUserData(null);
    }
  }, []); 

  // Define el método updateUserData para actualizar el contexto del usuario
  const updateUserData = (newUserData: Partial<UserData>) => {
    setUserData((prevUserData) => prevUserData ? { ...prevUserData, ...newUserData } : null);
  };

  useEffect(() => {
    if (isAuthenticated === null) {
      syncAuthStatus();
    }
  }, [syncAuthStatus, isAuthenticated]);

  const logout = () => {
    localStorage.removeItem('token');
    setIsAuthenticated(false);
    setUserData(null);
    setHasOrganization(null);
  };

  const authValue = useMemo(() => ({
    isAuthenticated,
    hasOrganization,
    userData,
    syncAuthStatus,
    updateUserData,
    logout,
    hasSubscription,
    subscriptionStatus,  // Añadido aquí
  }), [isAuthenticated, hasOrganization, userData, hasSubscription, subscriptionStatus, syncAuthStatus]);

  return (
    <AuthContext.Provider value={authValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
